"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, Skeleton, } from "@owlcat/ui";
import { useSelfProfile } from "./useSelfProfile";
/**
 * ProfileControl — the shared "who am I" header control for the OwlCat fleet.
 *
 * Purely CLIENT-SIDE so the host page can stay fully static. It resolves the
 * current identity via `useSelfProfile` (credentialed fetch to the accounts
 * self-profile endpoint, parent-domain `owlcat-id` cookie), then renders:
 *
 *   • 200  → avatar + name, with a menu: accounts profile · your apps · sign out.
 *   • 401 / network error / CORS-blocked → the "Sign in" button (→ accounts).
 *
 * SELF-CONTAINED: it takes all URLs + labels as props and depends ONLY on
 * @owlcat/ui — no next-intl, no app-local imports — so every app header can
 * import it verbatim from @owlcat/identity-react.
 */
const stripSlash = (u) => u.replace(/\/+$/, "");
/** `${accountsUrl}${path}` with optional `?app=<appId>&return=<returnTo>`.
 *  `app` is REQUIRED for /login to validate `return` against that app's
 *  allowedReturnOrigins — without it the return is dropped and the user is left
 *  on accounts after OAuth. */
function endpoint(accountsUrl, path, returnTo, appId) {
    const base = `${stripSlash(accountsUrl)}${path.startsWith("/") ? path : `/${path}`}`;
    const q = new URLSearchParams();
    if (appId)
        q.set("app", appId);
    if (returnTo)
        q.set("return", returnTo);
    const qs = q.toString();
    if (!qs)
        return base;
    return `${base}${base.includes("?") ? "&" : "?"}${qs}`;
}
function initials(profile) {
    const src = (profile.name || profile.email || "?").trim();
    const parts = src.split(/\s+/).filter(Boolean);
    const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : src.slice(0, 2);
    return chars.toUpperCase();
}
export function ProfileControl({ accountsUrl, appId, locale, selfPath = "/me", loginPath = "/login", logoutPath = "/logout", profileUrl, returnTo, signOutRedirect = "/", initialProfile, knownAnonymous, labels, signInVariant = "default", compact = false, headerExtras, menuExtras, anonExtras, }) {
    const selfProfileUrl = endpoint(accountsUrl, selfPath);
    // Pass appId so the /me fetch carries `X-Owlcat-App` → accounts best-effort
    // stamps identity_app_usage on use (activation tracking for cookie-local apps).
    const { state, markSignedOut } = useSelfProfile(selfProfileUrl, appId, initialProfile, knownAnonymous);
    // The fleet app directory (public, CORS) → "jump to another app" links in the
    // menu. Fetched once; the CURRENT app is filtered out. Best-effort (empty on
    // failure → the section just doesn't render). Hook stays before any early
    // return (rules-of-hooks).
    const [apps, setApps] = useState([]);
    useEffect(() => {
        let live = true;
        const url = new URL(endpoint(accountsUrl, "/apps"), typeof window !== "undefined" ? window.location.href : "http://localhost");
        if (locale)
            url.searchParams.set("locale", locale);
        fetch(url.toString(), { headers: { Accept: "application/json" } })
            .then((r) => (r.ok ? r.json() : []))
            .then((list) => {
            if (live && Array.isArray(list))
                setApps(list);
        })
            .catch(() => {
            /* leave [] */
        });
        return () => {
            live = false;
        };
    }, [accountsUrl, locale]);
    const otherApps = apps.filter((a) => a.app_id !== appId && a.url);
    if (state.status === "loading") {
        return _jsx(Skeleton, { className: "h-10 w-10 rounded-full" });
    }
    if (state.status === "anon" && compact) {
        return (_jsx(Button, { asChild: true, variant: "ghost", size: "icon", className: "h-10 w-10", children: _jsx("a", { href: endpoint(accountsUrl, loginPath, returnTo, appId), "aria-label": labels.signIn, title: labels.signIn, "data-testid": "profile-sign-in", children: _jsxs("svg", { viewBox: "0 0 24 24", className: "h-5 w-5", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" }), _jsx("path", { d: "M10 17l5-5-5-5" }), _jsx("path", { d: "M15 12H3" })] }) }) }));
    }
    if (state.status === "anon") {
        // Anon has no dropdown to hold language/theme → the caller can pass a compact
        // `anonExtras` control to sit beside the Sign-in button.
        return (_jsxs("div", { className: "flex items-center gap-2", children: [anonExtras, _jsx(Button, { asChild: true, variant: signInVariant, children: _jsx("a", { href: endpoint(accountsUrl, loginPath, returnTo, appId), "data-testid": "profile-sign-in", children: labels.signIn }) })] }));
    }
    // accounts logout is POST-only (it bumps session_epoch + clears the shared
    // cookie); a GET link would 405. POST it credentialed, flip to signed-out, then
    // leave the (now private) page for the app landing with a FULL navigation so the
    // app's proxy sees no fleet cookie and drops its own session too.
    const handleSignOut = async () => {
        try {
            await fetch(endpoint(accountsUrl, logoutPath, returnTo, appId), {
                method: "POST",
                credentials: "include",
            });
        }
        catch {
            // ignore — clear the UI regardless; the cookie is parent-domain httpOnly.
        }
        markSignedOut();
        if (signOutRedirect != null && typeof window !== "undefined") {
            window.location.assign(signOutRedirect);
        }
    };
    const { profile } = state;
    const display = profile.name || profile.email || labels.account;
    return (_jsxs("div", { className: "flex items-center gap-2", children: [headerExtras, _jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs(Button, { variant: "ghost", className: compact ? "h-10 w-10 p-0" : "h-10 gap-2 px-2", "aria-label": display, "data-testid": "profile-menu", "data-identity": profile.identityId, children: [_jsxs(Avatar, { className: "h-8 w-8", children: [profile.avatar ? _jsx(AvatarImage, { src: profile.avatar, alt: "" }) : null, _jsx(AvatarFallback, { className: "text-xs font-semibold", children: initials(profile) })] }), compact ? null : (_jsx("span", { className: "hidden max-w-[10rem] truncate text-sm font-medium sm:inline", children: display }))] }) }), _jsxs(DropdownMenuContent, { side: compact ? "right" : "bottom", align: "end", className: "w-64", children: [_jsx(DropdownMenuLabel, { className: "truncate", children: display }), _jsx(DropdownMenuSeparator, {}), _jsx(DropdownMenuItem, { asChild: true, children: _jsx("a", { href: profileUrl, children: labels.profile }) }), menuExtras, otherApps.length > 0 && (_jsxs(_Fragment, { children: [_jsx(DropdownMenuSeparator, {}), _jsxs(DropdownMenuSub, { children: [_jsx(DropdownMenuSubTrigger, { children: labels.yourApps ?? "Apps" }), _jsx(DropdownMenuSubContent, { children: otherApps.map((a) => (_jsx(DropdownMenuItem, { asChild: true, children: _jsxs("a", { href: a.url, target: "_blank", rel: "noopener noreferrer", className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: "truncate", children: a.name }), _jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", className: "h-3.5 w-3.5 shrink-0 text-muted-foreground", children: [_jsx("path", { d: "M15 3h6v6" }), _jsx("path", { d: "M10 14 21 3" }), _jsx("path", { d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" })] })] }) }, a.app_id))) })] })] })), _jsx(DropdownMenuSeparator, {}), _jsx(DropdownMenuItem, { onSelect: (e) => {
                                    e.preventDefault();
                                    void handleSignOut();
                                }, children: labels.signOut })] })] })] }));
}
